/**
 * LiveUpdatesModal Components
 *
 * Separate modal components for Application, API, and Query logs
 * Features: WebSocket live streaming, stream/table toggle, connection status
 * User preferences: Stream view default, start fresh (no pre-population)
 */

import { useState, useEffect, useMemo } from 'react';
import { IconList, IconTable, IconTrash } from '@tabler/icons-react';
import {
  Modal,
  Stack,
  Group,
  Badge,
  Button,
  Text,
  Card,
  Code,
  ScrollArea,
} from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { useApplicationLogs, useApiLogs, useQueryLogs } from '@actschurch/ccms-sdk';
import type {
  ApplicationLogsLiveModalProps,
  ApiLogsLiveModalProps,
  QueryLogsLiveModalProps,
  AppLogRow,
  ApiLogRow,
  QueryLogRow,
} from '../types';
import type {
  ApplicationLogEntry,
  ApiRequestLogEntry,
  QueryPerformanceLogEntry,
} from '@actschurch/ccms-sdk';

// Helper: Format timestamp
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Helper: Get log level color with distinct colors
const getLogLevelColor = (level: string): string => {
  switch (level.toLowerCase()) {
    case 'critical':
      return 'red';
    case 'error':
      return 'pink';
    case 'warning':
      return 'orange';
    case 'information':
    case 'info':
      return 'cyan';
    case 'debug':
      return 'grape';
    default:
      return 'gray';
  }
};

// Helper: Get HTTP method color (Swagger style)
const getHttpMethodColor = (method: string): string => {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'blue';      // Swagger GET - blue
    case 'POST':
      return 'green';     // Swagger POST - green
    case 'PUT':
      return 'orange';    // Swagger PUT - orange
    case 'DELETE':
      return 'red';       // Swagger DELETE - red
    case 'PATCH':
      return 'teal';      // Swagger PATCH - teal
    case 'OPTIONS':
      return 'violet';    // Swagger OPTIONS - violet
    case 'HEAD':
      return 'grape';     // Swagger HEAD - grape
    default:
      return 'gray';
  }
};

// Helper: Get status code color with distinct colors for each status range
const getStatusCodeColor = (code: number): string => {
  // 2xx Success - shades of green
  if (code === 200) return 'lime';       // OK
  if (code === 201) return 'teal';       // Created
  if (code === 204) return 'green';      // No Content
  if (code >= 200 && code < 300) return 'cyan';  // Other 2xx

  // 3xx Redirect - shades of blue
  if (code === 301) return 'indigo';     // Moved Permanently
  if (code === 302) return 'blue';       // Found
  if (code >= 300 && code < 400) return 'violet';  // Other 3xx

  // 4xx Client Error - shades of yellow/orange
  if (code === 400) return 'yellow';     // Bad Request
  if (code === 401) return 'orange';     // Unauthorized
  if (code === 403) return 'orange';     // Forbidden
  if (code === 404) return 'yellow';     // Not Found
  if (code >= 400 && code < 500) return 'orange';  // Other 4xx

  // 5xx Server Error - shades of red
  if (code === 500) return 'red';        // Internal Server Error
  if (code === 502) return 'pink';       // Bad Gateway
  if (code === 503) return 'red';        // Service Unavailable
  if (code >= 500) return 'red';         // Other 5xx

  return 'gray';
};

// Helper: Format duration
const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

// ==================== APPLICATION LOGS LIVE MODAL ====================

export function ApplicationLogsLiveModal({
  opened,
  onClose,
  serviceNames,
}: ApplicationLogsLiveModalProps) {
  const [showTable, setShowTable] = useState(false); // Default to stream view

  const {
    logs,
    isConnected,
    isSubscribed,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    clearLogs,
  } = useApplicationLogs({
    autoConnect: false, // Manual control
    autoLoadRecent: false, // Don't load recent logs (start fresh)
    maxBufferSize: 500,
  });

  // Connect when modal opens
  useEffect(() => {
    if (opened) {
      connect();
      subscribe({ serviceNames: serviceNames && serviceNames.length > 0 ? serviceNames : undefined });
    }
    return () => {
      if (opened) {
        unsubscribe();
        disconnect();
      }
    };
  }, [opened, serviceNames, connect, disconnect, subscribe, unsubscribe]);

  // Define columns for table view
  const columns: DataTableColumn<AppLogRow>[] = useMemo(
    () => [
      {
        key: 'timestamp',
        label: 'Timestamp',
        sortable: true,
        render: (value) => (
          <Text size="xs">{formatTimestamp(value as string)}</Text>
        ),
      },
      {
        key: 'level',
        label: 'Level',
        render: (value) => (
          <Badge color={getLogLevelColor(value as string)} size="sm">
            {value as string}
          </Badge>
        ),
      },
      {
        key: 'serviceName',
        label: 'Service',
        render: (value) => <Text size="xs">{(value as string) || '-'}</Text>,
      },
      {
        key: 'message',
        label: 'Message',
        render: (value) => (
          <Text size="sm" lineClamp={2}>
            {value as string}
          </Text>
        ),
      },
    ],
    []
  );

  // Convert logs to rows
  const rows: AppLogRow[] = useMemo(() => {
    return logs.map((log) => ({
      ...log,
      id: log.id,
      logLevel: log.level,
      category: log.category || '',
    }));
  }, [logs]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Live Application Log Updates"
      size="xl"
      variant="elevated"
    >
      <Stack gap="md">
        {/* Connection Status */}
        <Group gap="sm">
          <Badge color={isConnected ? 'green' : 'red'} variant="dot">
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
          <Badge color={isSubscribed ? 'blue' : 'gray'} variant="dot">
            {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
          </Badge>
        </Group>

        {/* View Toggle and Actions */}
        <Group gap="sm">
          <Button
            variant={showTable ? 'light' : 'filled'}
            onClick={() => setShowTable(!showTable)}
            leftSection={showTable ? <IconList size={16} /> : <IconTable size={16} />}
          >
            {showTable ? 'Show Stream' : 'Show Table'}
          </Button>
          <Button onClick={clearLogs} variant="light" color="red" leftSection={<IconTrash size={16} />}>
            Clear
          </Button>
        </Group>

        {/* Log Display - Stream View by Default */}
        {!showTable ? (
          <ScrollArea h={500} type="auto">
            <Stack gap="xs">
              {logs.length === 0 ? (
                <Text ta="center" c="dimmed" py="xl">
                  Waiting for logs...
                </Text>
              ) : (
                logs.map((log, idx) => (
                  <Card key={idx} withBorder p="sm">
                    <Group gap="xs" mb="xs">
                      <Badge color={getLogLevelColor(log.level)} size="sm" variant="filled">
                        {log.level}
                      </Badge>
                      <Text size="xs" c="dimmed">
                        {formatTimestamp(log.timestamp)}
                      </Text>
                      {log.serviceName && (
                        <Badge variant="light" size="sm">
                          {log.serviceName}
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm">{log.message}</Text>
                    {log.correlationId && (
                      <Code size="xs" mt="xs">
                        {log.correlationId}
                      </Code>
                    )}
                  </Card>
                ))
              )}
            </Stack>
          </ScrollArea>
        ) : (
          <DataTable<AppLogRow>
            data={rows}
            columns={columns}
            stickyHeader
            highlightOnHover
            emptyMessage="No logs received yet..."
            enableInternalSort
            defaultSortKey="timestamp"
            defaultSortDirection="desc"
          />
        )}

        <Text size="sm" c="dimmed">
          {logs.length} logs received
        </Text>
      </Stack>
    </Modal>
  );
}

// ==================== API LOGS LIVE MODAL ====================

export function ApiLogsLiveModal({
  opened,
  onClose,
  serviceNames,
}: ApiLogsLiveModalProps) {
  const [showTable, setShowTable] = useState(false);

  const {
    logs,
    isConnected,
    isSubscribed,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    clearLogs,
  } = useApiLogs({
    autoConnect: false,
    autoLoadRecent: false,
    maxBufferSize: 500,
  });

  useEffect(() => {
    if (opened) {
      connect();
      subscribe({ serviceNames: serviceNames && serviceNames.length > 0 ? serviceNames : undefined });
    }
    return () => {
      if (opened) {
        unsubscribe();
        disconnect();
      }
    };
  }, [opened, serviceNames, connect, disconnect, subscribe, unsubscribe]);

  const columns: DataTableColumn<ApiLogRow>[] = useMemo(
    () => [
      {
        key: 'timestamp',
        label: 'Timestamp',
        sortable: true,
        render: (value) => (
          <Text size="xs">{formatTimestamp(value as string)}</Text>
        ),
      },
      {
        key: 'httpMethod',
        label: 'Method',
        render: (value) => <Badge size="sm">{value as string}</Badge>,
      },
      {
        key: 'path',
        label: 'Path',
        render: (value) => (
          <Text size="sm" lineClamp={1}>
            {value as string}
          </Text>
        ),
      },
      {
        key: 'statusCode',
        label: 'Status',
        render: (value) => (
          <Badge color={getStatusCodeColor(value as number)} size="sm">
            {value as number}
          </Badge>
        ),
      },
      {
        key: 'durationMs',
        label: 'Duration',
        render: (value) => <Text size="xs">{formatDuration(value as number)}</Text>,
      },
    ],
    []
  );

  const rows: ApiLogRow[] = useMemo(() => {
    return logs.map((log) => ({ ...log, id: log.id }));
  }, [logs]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Live API Request Log Updates"
      size="xl"
      variant="elevated"
    >
      <Stack gap="md">
        <Group gap="sm">
          <Badge color={isConnected ? 'green' : 'red'} variant="dot">
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
          <Badge color={isSubscribed ? 'blue' : 'gray'} variant="dot">
            {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
          </Badge>
        </Group>

        <Group gap="sm">
          <Button
            variant={showTable ? 'light' : 'filled'}
            onClick={() => setShowTable(!showTable)}
            leftSection={showTable ? <IconList size={16} /> : <IconTable size={16} />}
          >
            {showTable ? 'Show Stream' : 'Show Table'}
          </Button>
          <Button onClick={clearLogs} variant="light" color="red" leftSection={<IconTrash size={16} />}>
            Clear
          </Button>
        </Group>

        {!showTable ? (
          <ScrollArea h={500} type="auto">
            <Stack gap="xs">
              {logs.length === 0 ? (
                <Text ta="center" c="dimmed" py="xl">
                  Waiting for API logs...
                </Text>
              ) : (
                logs.map((log, idx) => (
                  <Card key={idx} withBorder p="sm">
                    <Group gap="xs" mb="xs">
                      <Badge color={getHttpMethodColor(log.httpMethod)} size="sm" variant="filled">
                        {log.httpMethod}
                      </Badge>
                      <Badge color={getStatusCodeColor(log.statusCode)} size="sm" variant="filled">
                        {log.statusCode}
                      </Badge>
                      <Text size="xs" c="dimmed">
                        {formatTimestamp(log.timestamp)}
                      </Text>
                      <Badge variant="light" size="sm" color={log.durationMs > 2000 ? 'red' : log.durationMs > 1000 ? 'orange' : log.durationMs > 500 ? 'yellow' : 'lime'}>
                        {formatDuration(log.durationMs)}
                      </Badge>
                    </Group>
                    <Text size="sm">{log.path}</Text>
                    {log.clientIp && (
                      <Text size="xs" c="dimmed" mt="xs">
                        IP: {log.clientIp}
                      </Text>
                    )}
                  </Card>
                ))
              )}
            </Stack>
          </ScrollArea>
        ) : (
          <DataTable<ApiLogRow>
            data={rows}
            columns={columns}
            stickyHeader
            highlightOnHover
            emptyMessage="No API logs received yet..."
            enableInternalSort
            defaultSortKey="timestamp"
            defaultSortDirection="desc"
          />
        )}

        <Text size="sm" c="dimmed">
          {logs.length} requests received
        </Text>
      </Stack>
    </Modal>
  );
}

// ==================== QUERY LOGS LIVE MODAL ====================

export function QueryLogsLiveModal({
  opened,
  onClose,
  serviceNames,
}: QueryLogsLiveModalProps) {
  const [showTable, setShowTable] = useState(false);

  const {
    logs,
    isConnected,
    isSubscribed,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    clearLogs,
  } = useQueryLogs({
    autoConnect: false,
    autoLoadRecent: false,
    maxBufferSize: 500,
  });

  useEffect(() => {
    if (opened) {
      connect();
      subscribe({ serviceNames: serviceNames && serviceNames.length > 0 ? serviceNames : undefined });
    }
    return () => {
      if (opened) {
        unsubscribe();
        disconnect();
      }
    };
  }, [opened, serviceNames, connect, disconnect, subscribe, unsubscribe]);

  const columns: DataTableColumn<QueryLogRow>[] = useMemo(
    () => [
      {
        key: 'timestamp',
        label: 'Timestamp',
        sortable: true,
        render: (value) => (
          <Text size="xs">{formatTimestamp(value as string)}</Text>
        ),
      },
      {
        key: 'queryType',
        label: 'Query Type',
        render: (value) => <Badge size="sm">{value as string}</Badge>,
      },
      {
        key: 'entityType',
        label: 'Entity',
        render: (value) => <Text size="xs">{(value as string) || '-'}</Text>,
      },
      {
        key: 'durationMs',
        label: 'Duration',
        render: (value) => <Text size="xs">{formatDuration(value as number)}</Text>,
      },
      {
        key: 'rowCount',
        label: 'Rows',
        render: (value) => <Text size="xs">{(value as number).toLocaleString()}</Text>,
      },
      {
        key: 'isSlowQuery',
        label: 'Slow',
        render: (value) =>
          value ? (
            <Badge size="sm" color="orange">
              Slow
            </Badge>
          ) : null,
      },
    ],
    []
  );

  const rows: QueryLogRow[] = useMemo(() => {
    return logs.map((log) => ({ ...log, id: log.id }));
  }, [logs]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Live Query Performance Log Updates"
      size="xl"
      variant="elevated"
    >
      <Stack gap="md">
        <Group gap="sm">
          <Badge color={isConnected ? 'green' : 'red'} variant="dot">
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
          <Badge color={isSubscribed ? 'blue' : 'gray'} variant="dot">
            {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
          </Badge>
        </Group>

        <Group gap="sm">
          <Button
            variant={showTable ? 'light' : 'filled'}
            onClick={() => setShowTable(!showTable)}
            leftSection={showTable ? <IconList size={16} /> : <IconTable size={16} />}
          >
            {showTable ? 'Show Stream' : 'Show Table'}
          </Button>
          <Button onClick={clearLogs} variant="light" color="red" leftSection={<IconTrash size={16} />}>
            Clear
          </Button>
        </Group>

        {!showTable ? (
          <ScrollArea h={500} type="auto">
            <Stack gap="xs">
              {logs.length === 0 ? (
                <Text ta="center" c="dimmed" py="xl">
                  Waiting for query logs...
                </Text>
              ) : (
                logs.map((log, idx) => (
                  <Card key={idx} withBorder p="sm">
                    <Group gap="xs" mb="xs">
                      <Badge size="sm">{log.queryType}</Badge>
                      {log.isSlowQuery && (
                        <Badge size="sm" color="orange">
                          Slow
                        </Badge>
                      )}
                      <Text size="xs" c="dimmed">
                        {formatTimestamp(log.timestamp)}
                      </Text>
                      <Badge variant="light" size="sm" color={log.durationMs > 2000 ? 'red' : log.durationMs > 1000 ? 'orange' : log.durationMs > 500 ? 'yellow' : 'lime'}>
                        {formatDuration(log.durationMs)}
                      </Badge>
                    </Group>
                    <Text size="sm">
                      {log.entityType || 'Unknown entity'} - {log.rowCount.toLocaleString()} rows
                    </Text>
                    {log.serviceName && (
                      <Text size="xs" c="dimmed" mt="xs">
                        Service: {log.serviceName}
                      </Text>
                    )}
                  </Card>
                ))
              )}
            </Stack>
          </ScrollArea>
        ) : (
          <DataTable<QueryLogRow>
            data={rows}
            columns={columns}
            stickyHeader
            highlightOnHover
            emptyMessage="No query logs received yet..."
            enableInternalSort
            defaultSortKey="timestamp"
            defaultSortDirection="desc"
          />
        )}

        <Text size="sm" c="dimmed">
          {logs.length} queries received
        </Text>
      </Stack>
    </Modal>
  );
}
